import {
  useState, useEffect, useRef, ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Chip, { ChipProps } from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';

interface Column {
  id: string;
  label: string;
}

interface ReportRow {
  productId?: string | number;
  productName?: string;
  color?: string;
  movementCategory: string;
  weeklyVelocity?: number;
  weeksOfSupply?: number | null;
  sellThroughRate?: number;
  hasInsufficientData?: boolean;
  [key: string]: unknown;
}

interface UnifiedReport {
  rows?: ReportRow[];
  total?: number;
  collectionId?: string;
  [key: string]: unknown;
}

interface UnifiedTableCollection {
  id: string;
  name: string;
}

interface TileFilter {
  field?: string;
  value?: string;
}

interface UnifiedTableProps {
  collection: UnifiedTableCollection;
  report?: UnifiedReport | undefined;
  getAnalytics: (arg: Record<string, unknown>) => Promise<{ data?: unknown; error?: unknown }>;
  queryOptions: Record<string, unknown> & { pageSize?: number };
  tileFilter?: TileFilter | null;
}

const MOVEMENT_COLORS: Record<string, ChipProps['color']> = {
  fast: 'success',
  normal: 'warning',
  slow: 'error',
};

const COLUMNS: Column[] = [
  { id: 'productName', label: 'Style' },
  { id: 'color', label: 'Color' },
  { id: 'unitsSold', label: 'Units Sold' },
  { id: 'inStockCount', label: 'In Stock' },
  { id: 'sellThroughRate', label: 'Sell-Through %' },
  { id: 'weeklyVelocity', label: 'Weekly Velocity' },
  { id: 'projectedDemand', label: 'Projected Demand' },
  { id: 'inProductionCount', label: 'In Production' },
  { id: 'availableSupply', label: 'Available Supply' },
  { id: 'weeksOfSupply', label: 'Weeks of Supply' },
  { id: 'movementCategory', label: 'Movement' },
];

const FORECAST_COLS = new Set(['weeklyVelocity', 'projectedDemand', 'weeksOfSupply']);

function renderCell(col: Column, row: ReportRow): ReactNode {
  if (col.id === 'sellThroughRate') {
    return `${row.sellThroughRate}%`;
  }
  if (col.id === 'movementCategory') {
    return (
      <Chip
        label={row.movementCategory.charAt(0).toUpperCase() + row.movementCategory.slice(1)}
        color={MOVEMENT_COLORS[row.movementCategory] || 'default'}
        size="small"
      />
    );
  }
  // Forecast-derived columns are unreliable without sufficient history — mask them only.
  if (FORECAST_COLS.has(col.id) && row.hasInsufficientData) {
    return '—';
  }
  if (col.id === 'weeklyVelocity') {
    return Number(row.weeklyVelocity).toFixed(1);
  }
  if (col.id === 'weeksOfSupply') {
    return row.weeksOfSupply !== null ? row.weeksOfSupply : '∞';
  }
  return row[col.id] as ReactNode;
}

export default function UnifiedTable({
  collection, report: initialReport, getAnalytics, queryOptions, tileFilter,
}: UnifiedTableProps) {
  const navigate = useNavigate();
  const [report, setReport] = useState(initialReport);
  const [skip, setSkip] = useState(true);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState('productName');
  const [pageIndex, setPageIndex] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const prevTileFilterRef = useRef(tileFilter);

  useEffect(() => {
    setReport(initialReport);
    setPageIndex(0);
    // If a tile filter is active, the parent's refetch was unfiltered — re-fetch with filter
    if (tileFilter) setRefreshKey((k) => k + 1);
  }, [initialReport]);

  useEffect(() => {
    if (skip) { setSkip(false); return; }

    const filterChanged = prevTileFilterRef.current !== tileFilter;
    prevTileFilterRef.current = tileFilter;

    // When filter changes while not on page 0, reset page — the pageIndex change will re-trigger
    if (filterChanged && pageIndex !== 0) {
      setPageIndex(0);
      return;
    }

    void getAnalytics({
      ...queryOptions,
      collections: [collection.id],
      order,
      orderBy,
      page: filterChanged ? 0 : pageIndex,
      tileFilterField: tileFilter?.field,
      tileFilterValue: tileFilter?.value,
    }).then((res) => {
      if (res.error) return;
      const { reports } = res.data as { reports: UnifiedReport[] };
      const newReport = reports.find((r) => r.collectionId === collection.id);
      if (newReport) setReport(newReport);
    });
  }, [order, orderBy, pageIndex, tileFilter, refreshKey]);

  if (!report) return null;

  const pageSize = queryOptions.pageSize || 30;
  const rows = report.rows || [];
  const pageCount = Math.ceil((report.total || 0) / pageSize);

  function handleSort(colId: string) {
    const isAsc = orderBy === colId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(colId);
  }

  function handleRowClick(row: ReportRow) {
    void navigate(`/shop/add?productId=${row.productId}&inventory=true`);
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
        {collection.name}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell key={col.id}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow
                key={`${row.productId}-${row.color}-${i}`}
                hover
                sx={{ cursor: 'pointer', bgcolor: i % 2 !== 0 ? 'action.hover' : 'background.paper' }}
                onClick={() => handleRowClick(row)}
              >
                {COLUMNS.map((col) => (
                  <TableCell key={col.id}>
                    {renderCell(col, row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ color: 'text.secondary' }}>
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Pagination
            count={pageCount}
            page={pageIndex + 1}
            onChange={(_, page) => setPageIndex(page - 1)}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
}
